/* eslint-disable no-unused-vars */
import { createSlice } from '@reduxjs/toolkit';

import history from '@history';
import _ from '@lodash';
import { setDefaultSettings } from 'app/store/fuse/settingsSlice';
import { showMessage } from 'app/store/fuse/messageSlice';

import jwtService from 'app/services/jwtService';
import { formatUserFullName } from 'app/main/utils/usersUtils';

/**
 * Nobody, which is what a signed-out session is.
 *
 * This shipped as the Fuse demo account, and the name and address it carried are a
 * live account at this company. Anything that resets the store — signing out, or a
 * session the API stopped trusting — therefore painted that person's name and email
 * into the toolbar for as long as a screen stayed mounted, so somebody watching saw
 * their own session turn into a colleague's. A blank identity cannot be mistaken for
 * anyone.
 *
 * `displayName` is an empty string rather than absent because `UserMenu` reads
 * `displayName[0]` for the avatar initial.
 */
const initialState = {
  role: [], // guest
  data: {
    displayName: '',
    photoURL: '',
    email: ''
  }
};

const userSlice = createSlice({
  name: 'auth/user',
  initialState,
  reducers: {
    setUser: (state, action) => action.payload,
    userLoggedOut: (state, action) => initialState
  },
  extraReducers: {}
});

export const { setUser, userLoggedOut } = userSlice.actions;

/**
 * `role` holds the account's **permissions**, not its role.
 *
 * Fuse's `hasPermission` asks "does the user hold any of the things this route lists",
 * which is exactly the question a permission list answers — it was only ever called a
 * role because roles used to be the only capability the platform had. Putting the
 * permissions in that slot means every route guard, navigation item and search entry
 * in `@fuse/` keeps working untouched, and a route config can now say what it needs
 * (`auth: ['payroll.read.all']`) instead of listing the job titles that happen to have
 * it.
 *
 * The role itself lives on `data.role` and `data.roleName`, for the places that want
 * to show it rather than act on it.
 *
 * The list comes from the API. Deriving it here from the role key would mean a second
 * copy of the permission catalogue in this repository, and the two would disagree
 * within a month of the first change.
 */
export const setUserData = userData => async (dispatch, getState) => {
  dispatch(setDefaultSettings({}));

  dispatch(
    setUser({
      role: userData.permissions || [],
      data: {
        ...userData,
        displayName: formatUserFullName(userData)
      }
    })
  );
};

// So far this is only used to update settings, but may be used for other data as avatar, password, etc.-
export const updateUserData = userData => async (dispatch, getState) => {
  const { user } = getState().auth;
  if (!user.role || user.role.length === 0) {
    // is guest
    return;
  }

  jwtService
    .updateUserData(userData)
    .then(() => {
      // dispatch(showMessage({ message: 'User data saved with api' }));
    })
    .catch(error => {
      dispatch(showMessage({ message: error.message }));
    });
};

export const updateUserSettings = settings => async (dispatch, getState) => {
  const oldUserData = _.get(getState(), 'auth.user.data', { settings: {} });
  const userData = {
    ...oldUserData,
    settings: {
      ...oldUserData.settings,
      ...settings
    }
  };
  dispatch(updateUserData(userData));

  return dispatch(setUserData(userData));
};

export const updateUserShortcuts = shortcuts => async (dispatch, getState) => {
  const { user } = getState().auth;
  const newUser = {
    ...user,
    data: {
      ...user.data,
      shortcuts
    }
  };

  dispatch(updateUserData(user));

  return dispatch(setUserData(newUser));
};

export const logoutUser = (message = null) => async (dispatch, getState) => {
  history.push({
    pathname: '/login'
  });
  jwtService.logout(message);
  dispatch(userLoggedOut());
};

export default userSlice.reducer;
